import { useCallback, useContext, useEffect, useState } from 'react';

import { Formik, FormikErrors, FormikHelpers } from 'formik';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { Button, Card, Checkbox, FormControlLabel, Grid, styled, Typography } from '@mui/material';

import { SubmitButton } from '../../../components/buttons';
import { FormContainer } from '../../../components/forms';
import { BrowseUpload, TextField } from '../../../components/inputs';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { Spinner } from '../../../components/spinners';
import { Defaults, ErrorMessages } from '../../../constants';
import { CategoryUpdateDTO, useGetCategory, useUpdateCategory } from '../../../lib/category/hooks';
import useMediaUpload from '../../../lib/media/hooks/useMediaUpload';
import { SubCategoryDto } from '../../../lib/sub-category/hooks';
import { checkFileSize } from '../../../lib/util/helpers';
import { Media } from '../../../types/Media';

import ConvertToSubCategoryButton from './ConvertToSubCategoryButton';

function CategoryEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const categoryId = id ?? '';
  const { data: category, isLoading: isLoadingCategory } = useGetCategory(categoryId);
  const { mutateAsync: updateCategory, isLoading: isLoadingUpdate } = useUpdateCategory();

  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);
  const [photosErrorMsg, setPhotosErrorMsg] = useState<string>('');
  const [uploadingFieldName, setUploadingFieldName] = useState<string | null>(null);
  const [, setAdditionalSubCategoryCount] = useState(0);
  const {
    upload,
    error: uploadError,
    isUploading,
  } = useMediaUpload({
    onError: () => {
      setAlertSnackbar({
        message: ErrorMessages.SERVER_ERROR,
        severity: 'error',
      });
      setPhotosErrorMsg(ErrorMessages.SERVER_ERROR);
    },
  });

  useEffect(() => {
    if (!uploadError) return;
    setAlertSnackbar({
      message: ErrorMessages.SERVER_ERROR,
      severity: 'error',
    });
    setPhotosErrorMsg(ErrorMessages.SERVER_ERROR);
  }, [setAlertSnackbar, uploadError]);

  const handlePhotoUpload = useCallback(
    async (
      event: React.ChangeEvent<HTMLInputElement>,
      setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<CategoryFormType>>,
      name: string,
    ) => {
      const files = event.target.files;

      if (files && files[0]) {
        const file = files[0];
        setUploadingFieldName(name);
        try {
          checkFileSize(file);
          setPhotosErrorMsg('');

          // Upload photo to media table and S3
          const uploadedMedia = await upload(file);

          // Save uploaded media info to Formik
          await setFieldValue(name, uploadedMedia);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : ErrorMessages.SERVER_ERROR;

          setPhotosErrorMsg(errorMessage);
          setAlertSnackbar({
            message: errorMessage,
            severity: 'error',
          });
        } finally {
          setUploadingFieldName(null);
        }
      }
    },
    [setAlertSnackbar, upload],
  );

  const handleSubmit = async (values: CategoryFormType, formikHelpers?: FormikHelpers<CategoryFormType>) => {
    const categoryObj: CategoryUpdateDTO = {
      title: values.title,
      media: values.media,
      published: values.published === true,
      featured: initialValues.featured === null ? false : true,
    };

    const mapSubCategories = (items: SubCategoryDto[]) =>
      items.map((item) => ({
        id: item.id,
        title: item.title,
        media: item.media,
        parent: { id: categoryId, ...categoryObj },
        published: item.published,
      }));

    const allSubCategories = [...mapSubCategories(values.filteredSubCategories), ...mapSubCategories(values.additionalSubCategory)];
    const res = await updateCategory(
      {
        categoryId: values.id,
        params: {
          ...categoryObj,
          subCategory: allSubCategories,
        },
      },
      {
        onSuccess: () => {
          setAlertSnackbar({
            message: 'Category updated',
            severity: 'success',
            vertical: 'top',
          });
        },
      },
    );

    // After successful update, reset Formik initialValues so newly created sub-categories
    // move from `additionalSubCategory` into `filteredSubCategories` and the add-list clears.
    try {
      const newCategory = (res as any) || {
        ...values,
        subCategories: [...values.filteredSubCategories, ...values.additionalSubCategory],
      };

      const newInitialValues = {
        ...newCategory,
        additionalSubCategory: [],
        filteredSubCategories: newCategory.subCategories ?? newCategory.filteredSubCategories ?? [],
        published: newCategory.published === null ? false : true,
      };

      if (formikHelpers && typeof formikHelpers.resetForm === 'function') {
        formikHelpers.resetForm({ values: newInitialValues });
      }

      // reset additional sub category counter used for forcing re-renders
      setAdditionalSubCategoryCount(0);
    } catch (e) {}
  };

  let initialValues: any;

  if (category) {
    initialValues = {
      ...category,
      additionalSubCategory: [],
      filteredSubCategories: category.subCategories,
      published: category.published === null ? false : true,
    };
  }

  const setInitValue = (value?: string | boolean) => value || ' ';

  return (
    <Container>
      {isLoadingCategory && <Spinner />}
      {!isLoadingCategory && initialValues && (
        <>
          <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={CategoryEditSchema}
            validateOnBlur={true}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, setFieldValue, handleChange, handleBlur }) => (
              <StyledForm>
                <TopBar>
                  <Typography variant="h5">Category</Typography>
                </TopBar>
                <StyledCard>
                  <Grid container rowSpacing={2}>
                    <Grid item xs={12}>
                      <TextField name="id" label="ID" value={values.id} disabled helperText=" " />
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: values.media ? '30px' : '0' }}>
                      <Grid item xs={8}>
                        <BrowseUpload
                          error={touched.media && !!errors.media}
                          fileLabel="Upload Photo"
                          name="media"
                          handleFileInput={(event) => {
                            handlePhotoUpload(event, setFieldValue, 'media');
                          }}
                          handleBlur={handleBlur}
                          disabled={isUploading}
                          isUploading={isUploading && uploadingFieldName === 'media'}
                          errorMsg={photosErrorMsg}
                          helperText={errors.media && touched.media ? errors.media : null}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        {values.media ? (
                          <>
                            <Typography>Uploaded Photo</Typography>
                            <img src={values.media?.smallUrl} alt="" />
                          </>
                        ) : null}
                      </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                      <Grid item xs={8}>
                        <TextField
                          error={touched.title && !!errors.title}
                          type="text"
                          name="title"
                          label="Name"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.title}
                          helperText={setInitValue(touched.title && errors.title)}
                          disabled={isUploading}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              id="published"
                              name="published"
                              checked={values.published}
                              value={values.published || true}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          }
                          label="Enable"
                        />
                      </Grid>
                    </Grid>
                    <ConvertToSubCategoryButton categoryId={categoryId} />
                  </Grid>
                </StyledCard>

                <TopBar>
                  <Typography variant="h5">Create Sub Categories</Typography>
                </TopBar>
                <StyledCard>
                  <Grid container rowSpacing={2}>
                    {values.filteredSubCategories &&
                      values.filteredSubCategories.map((subCategory: SubCategoryDto, index: number) => {
                        let isPublished = values.filteredSubCategories?.[index]?.published ? true : false;
                        if (!values.published) isPublished = false;
                        return (
                          <Grid container key={`subCategory-media-${subCategory.id}-${index}`}>
                            <Grid container spacing={2} sx={{ mb: '30px' }}>
                              <Grid item xs={8}>
                                <BrowseUpload
                                  fileLabel="Upload Photo"
                                  name={`filteredSubCategories[${index}].media`}
                                  handleFileInput={(event) => {
                                    handlePhotoUpload(event, setFieldValue, `filteredSubCategories[${index}].media`);
                                  }}
                                  handleBlur={handleBlur}
                                  disabled={isUploading}
                                  isUploading={isUploading && uploadingFieldName === `filteredSubCategories[${index}].media`}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                {subCategory?.media ? (
                                  <>
                                    <Typography>Uploaded Photo</Typography>
                                    <img src={subCategory.media.smallUrl} alt="" />
                                  </>
                                ) : null}
                              </Grid>
                            </Grid>

                            <Grid container spacing={2}>
                              <Grid item xs={8}>
                                <TextField
                                  type="text"
                                  error={
                                    touched.filteredSubCategories?.[index]?.title &&
                                    !!(errors.filteredSubCategories?.[index] as FormikErrors<SubCategoryDto>)?.title
                                  }
                                  name={`filteredSubCategories[${index}].title`}
                                  value={values.filteredSubCategories?.[index]?.title}
                                  label="Sub Category Title"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  helperText={setInitValue(
                                    touched.filteredSubCategories?.[index]?.title &&
                                      (errors.filteredSubCategories?.[index] as FormikErrors<SubCategoryDto>)?.title,
                                  )}
                                  disabled={isLoadingUpdate}
                                  style={{
                                    paddingBottom: '20px',
                                  }}
                                />
                              </Grid>
                              <Grid item xs={3}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      id={`filteredSubCategories[${index}].published`}
                                      name={`filteredSubCategories[${index}].published`}
                                      value={isPublished}
                                      checked={isPublished}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      disabled={values.published ? false : true}
                                    />
                                  }
                                  label="Enable"
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        );
                      })}

                    {values.additionalSubCategory &&
                      values.additionalSubCategory.map((subCategory: SubCategoryDto, i: number) => {
                        return (
                          <Grid container key={`additional-subCategory-media-${subCategory.id}-${i}`}>
                            <Grid container spacing={2} sx={{ mb: subCategory?.media ? '30px' : '0' }}>
                              <Grid item xs={8}>
                                <BrowseUpload
                                  fileLabel="Upload Photo"
                                  name={`additionalSubCategory[${i}].media`}
                                  handleFileInput={(event) => {
                                    handlePhotoUpload(event, setFieldValue, `additionalSubCategory[${i}].media`);
                                  }}
                                  handleBlur={handleBlur}
                                  disabled={isUploading}
                                  isUploading={isUploading && uploadingFieldName === `additionalSubCategory[${i}].media`}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                {subCategory?.media ? (
                                  <>
                                    <Typography>Uploaded Photo</Typography>
                                    <img src={subCategory.media?.smallUrl} alt="" />
                                  </>
                                ) : null}
                              </Grid>
                            </Grid>

                            <Grid container spacing={2}>
                              <Grid item xs={8}>
                                <TextField
                                  error={
                                    touched.additionalSubCategory?.[i]?.title &&
                                    !!(errors.additionalSubCategory?.[i] as FormikErrors<SubCategoryDto>)?.title
                                  }
                                  type="text"
                                  name={`additionalSubCategory[${i}].title`}
                                  label="Sub Category Title"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  helperText={setInitValue(
                                    touched.additionalSubCategory?.[i]?.title &&
                                      (errors.additionalSubCategory?.[i] as FormikErrors<SubCategoryDto>)?.title,
                                  )}
                                  disabled={isUploading}
                                  style={{
                                    paddingBottom: '10px',
                                  }}
                                />
                              </Grid>
                              <Grid item xs={3}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      name={`additionalSubCategory.[${i}].published`}
                                      value={subCategory.published}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      disabled={values.published ? false : true}
                                    />
                                  }
                                  label="Enable"
                                />
                              </Grid>
                              <Grid item xs={1}>
                                <StyledButton
                                  onClick={() => {
                                    // remove item immutably via setFieldValue
                                    const newList = values.additionalSubCategory.filter((_, idx: number) => idx !== i);
                                    setFieldValue('additionalSubCategory', newList);
                                    setAdditionalSubCategoryCount((state: number) => state - 1);
                                  }}
                                  type="button"
                                >
                                  Remove
                                </StyledButton>
                              </Grid>
                            </Grid>
                          </Grid>
                        );
                      })}

                    <Grid item xs={12}>
                      <StyledButton
                        onClick={() => {
                          // use Formik's setFieldValue to update form state instead of mutating values directly
                          const newList = [...values.additionalSubCategory, subCategoryDefaultValue];
                          setFieldValue('additionalSubCategory', newList);
                          setAdditionalSubCategoryCount((state: number) => state + 1);
                        }}
                        type="button"
                      >
                        Add Sub Category
                      </StyledButton>
                    </Grid>
                  </Grid>
                </StyledCard>

                <ButtonContainer>
                  <Buttons>
                    <SubmitButton type="submit" disabled={isLoadingUpdate || isUploading} loading={isLoadingUpdate} variant="contained">
                      Save
                    </SubmitButton>
                    <Button onClick={() => navigate(-1)} disabled={isLoadingUpdate || isUploading} type="button">
                      Cancel
                    </Button>
                  </Buttons>
                </ButtonContainer>
              </StyledForm>
            )}
          </Formik>
        </>
      )}
    </Container>
  );
}

type CategoryFormType = {
  id: string;
  title: string;
  media?: Media;
  published: boolean;
  additionalSubCategory: SubCategoryDto[];
  filteredSubCategories: SubCategoryDto[];
};

const subCategoryDefaultValue: SubCategoryDto = {
  title: '',
  media: undefined,
  parent: {
    title: '',
    media: null,
    published: false,
  },
  published: false,
};

const CategoryEditSchema = Yup.object().shape({
  title: Yup.string().required(ErrorMessages.REQUIRED_CATEGORY_NAME),
  media: Yup.mixed().required(ErrorMessages.REQUIRED_PHOTO),
  additionalSubCategory: Yup.array().of(
    Yup.object().shape({
      title: Yup.string()
        .required(ErrorMessages.REQUIRED_SUBCATEGORY_TITLE)
        .max(Defaults.MAXIMUM_SUB_CATEGORY_LENGTH, ErrorMessages.INVALID_SUBCATEGORY_LENGTH),
      media: Yup.mixed().nullable(),
    }),
  ),
  filteredSubCategories: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().required('Sub Category Id is required'),
      title: Yup.string()
        .required(ErrorMessages.REQUIRED_SUBCATEGORY_TITLE)
        .max(Defaults.MAXIMUM_SUB_CATEGORY_LENGTH, ErrorMessages.INVALID_SUBCATEGORY_LENGTH),
      media: Yup.mixed().nullable(),
    }),
  ),
});

const Container = styled('div')({
  padding: '20px',
  backgroundColor: '#eee',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const TopBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
});

const StyledForm = styled(FormContainer)({
  paddingTop: '0 !important',
  paddingInline: '0 !important',
  paddingBottom: '0 !important',
});

const StyledCard = styled(Card)({
  display: 'flex',
  padding: '50px 40px 20px 40px',
  marginBottom: '32px',
});

const ButtonContainer = styled('div')({
  marginTop: '20px',
  alignSelf: 'stretch',
  flex: 1,
  display: 'flex',
  justifyContent: 'space-between',
});

const Buttons = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',

  '& > *': {
    margin: '5px',
  },
});

const StyledButton = styled(Button)({
  textTransform: 'capitalize',
});

export default CategoryEdit;
